import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  CarsAvailabilitySelectors,
  CoreCarsComponent,
} from "@b2bportal/core-cars";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import type { TimeAndPlace } from "@b2bportal/ground-api";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { CarAttributePill } from "@tenant/cars";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import { useCarsStyles } from "~core-cars/themes";

export type PickupInfoProps = {
  pickup: TimeAndPlace;
  distancePill?: JSX.Element | null;
} & PlatformComponentProps;
const COMPONENT_KEY = CoreCarsComponent.PickupPill;

export const PickupPill = ({
  className,
  children,
  pickup,
  distancePill,
}: PickupInfoProps) => {
  const styles = useCarsStyles(COMPONENT_KEY);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();
  const search = useSelector(CarsAvailabilitySelectors.getSearch);

  const addressOnlyPickupPill = useMemo(() => {
    if (!pickup.address) return null;

    return (
      <CarAttributePill icon={<Icon iconName={IconNameEnum.mapPin} />}>
        {pickup.address}
      </CarAttributePill>
    );
  }, [pickup.address]);

  const maybeDistance = useMemo(() => {
    if (search == null || pickup.metersToRequested == null) return null;
    const pickupLocation = search.pickupLocation;

    const distance = distancePill ?? (
      <CarAttributePill
        className={cn("distance-pill")}
        icon={<Icon iconName={IconNameEnum.mapPin} />}
      >
        <div>
          <Trans
            i18nKey="core-cars.distanceFromLocation"
            components={{ distance: <span className={cn("distance")} /> }}
            values={{
              distance: `${(pickup.metersToRequested / 1000).toFixed(1)} km`,
              location: pickupLocation.label,
            }}
          />
        </div>
      </CarAttributePill>
    );

    switch (pickupLocation.id.Id) {
      case "Flight": {
        return distance;
      }
      case "Grounds": {
        switch (pickupLocation.id.groundSelection.GroundSelection) {
          case "Place": {
            return pickupLocation.id.groundSelection.placeTypes.includes(
              "point_of_interest"
            )
              ? distance
              : null;
          }
          case "Location":
            return null;
          default:
            return ensureExhaustive(pickupLocation.id.groundSelection, null);
        }
      }
      default:
        return ensureExhaustive(pickupLocation.id, null);
    }
  }, [cn, distancePill, pickup.metersToRequested, search]);

  const pickupPill = useMemo(() => {
    if (!pickup.pickUpInformation) return addressOnlyPickupPill;

    if (pickup.pickUpInformation?.pickUpType != null) {
      switch (pickup.pickUpInformation.pickUpType) {
        case "Airport":
          return (
            <CarAttributePill
              icon={<Icon iconName={IconNameEnum.car__airport} />}
            >
              {t("core-cars.pickup.airport")}
            </CarAttributePill>
          );
        case "MeetAndGreet":
          return (
            <CarAttributePill
              icon={<Icon iconName={IconNameEnum.checkCircle} />}
            >
              {t("core-cars.pickup.meetAndGreet")}
            </CarAttributePill>
          );
        case "Shuttle":
          return (
            <CarAttributePill
              icon={<Icon iconName={IconNameEnum.car__airport} />}
            >
              {t("core-cars.pickup.shuttle")}
            </CarAttributePill>
          );
        default:
          return ensureExhaustive(
            pickup.pickUpInformation.pickUpType,
            maybeDistance
          );
      }
    } else {
      return maybeDistance;
    }
  }, [addressOnlyPickupPill, maybeDistance, pickup.pickUpInformation, t]);

  return pickupPill != null ? (
    <div className={clsx(block, className)}>
      {pickupPill} {children}
    </div>
  ) : null;
};
